export interface RetryWithValidationOptions<T> {
    /** The async operation to retry */
    operation: () => Promise<T>
    /** Function to validate the operation result */
    validator: (result: T) => boolean
    /** Maximum number of retry attempts */
    maxRetries: number
    /** Callback invoked when all retry attempts fail. Should return a result or throw an error */
    onFailure: (lastError: Error) => T
}

/**
 * Retries an async operation with validation until it succeeds or max retries are reached.
 * @returns The validated result or result of onFailure callback
 */
export async function retryWithValidation<T>({
    operation,
    validator,
    maxRetries,
    onFailure,
}: RetryWithValidationOptions<T>): Promise<T> {
    let lastError: Error | null = null
    let attempt = 0

    while (attempt < maxRetries) {
        try {
            const result = await operation()

            if (!validator(result)) {
                attempt++
                continue
            }

            return result
        } catch (error) {
            lastError = error as Error
            attempt++

            if (attempt >= maxRetries) {
                break
            }
        }
    }

    // All retries exhausted
    return onFailure(
        lastError ?? new Error(`Operation failed after ${maxRetries} attempts`),
    )
}
